<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-poll-people" class="q-mb-sm" /> {{ $t('surveys') }}
        </div>

        <q-table
            :data="surveys"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            row-key="name"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props" :props="props">
                <div class="col" />

                <q-btn color="secondary" icon="far fa-plus" :label="$t('new')" @click="$router.push({ name: 'admin_survey', params: { id: 0 } })" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="title" :props="props">
                    {{ props.row.title }}
                </q-td>

                <q-td key="permission" :props="props">
                    <q-btn color="primary" text-color="white" :label="getQuantity(props.row.permission)" no-ripple>
                        <q-tooltip>
                            <p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
                                {{ $t('areas') }}:
                                <span v-for="(area, index) in props.row.permission.areas" :key="index">
                                    {{ getArea(area) }}
                                    <span v-if="(props.row.permission.areas.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.clients.length > 0" class="tooltip q-mb-sm">
                                {{ $t('clients') }}:
                                <span v-for="(client, index) in props.row.permission.clients" :key="index">
                                    {{ getClient(client) }}
                                    <span v-if="(props.row.permission.clients.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.user_groups.length > 0" class="tooltip q-mb-sm">
                                {{ $t('user_groups') }}:
                                <span v-for="(user_group, index) in props.row.permission.user_groups" :key="index">
                                    {{ getUserGroup(user_group) }}
                                    <span v-if="(props.row.permission.user_groups.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.profiles.length > 0" class="tooltip q-mb-sm">
                                {{ $t('profiles') }}:
                                <span v-for="(profile, index) in props.row.permission.profiles" :key="index">
                                    {{ getUserProfile(profile) }}
                                    <span v-if="(props.row.permission.profiles.length - 1) > index">, </span>
                                </span>
                            </p>
                        </q-tooltip>
                    </q-btn>
                </q-td>

                <q-td key="survey_type" :props="props">
                    {{ $t(getSurveyType(props.row.survey_type)) }}
                </q-td>

                <q-td key="qty_survey_questions" :props="props">
                    {{ props.row.qty_survey_questions }}
                </q-td>

                <q-td key="start_date" :props="props">
                    {{ fDate(props.row.start_date) }}
                </q-td>

                <q-td key="end_date" :props="props">
                    {{ fDate(props.row.end_date) }}
                </q-td>

                <q-td key="status" :props="props" auto-width>
                    <q-chip small :icon="getIconStatus(props.row.status)" :color="getColorStatus(props.row.status)" class="full-width">
                        {{ $t(getStatus(props.row.status)) }}
                    </q-chip>
                </q-td>

                <q-td key="actions" :props="props">
                    <q-btn flat round color="primary" icon="far fa-pencil-alt" @click="$router.push({ name: 'admin_survey', params: { id: props.row.actions } })" >
                        <q-tooltip>
                            {{ $t('view_or_edit_tooltip')}}
                        </q-tooltip>
                    </q-btn>

                    <q-btn flat round color="secondary" icon="far fa-chart-line" @click="showReport(props.row)" >
                        <q-tooltip>
                            {{ $t('view_report_for_this_survey_tooltip')}}
                        </q-tooltip>
                    </q-btn>

                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" >
                        <q-tooltip>
                            {{ $t('delete_tooltip')}}
                        </q-tooltip>
                    </q-btn>
                </q-td>
            </q-tr>
        </q-table>

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props" :props="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>

        <report :show_report_survey="show_report_survey" :id="id" :title="title" @hide_report="hideReport" />
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import Report from 'components/admin/surveys/Report'
import { LocalStorage } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: [ "custom", "survey", "admin", "common"] },

    components: {
        'report': Report
    },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: null,
                descending: false,
                sortBy: null
            },
            loading: false,
            surveys: [],
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'name',
                    sortable: true,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'title',
                    align: 'left',
                    field: 'title',
                    sortable: true,
                },
                {
                    name: 'permission',
                    required: true,
                    label: 'permission',
                    align: 'left',
                    field: 'permission',
                    sortable: false,
                },
                {
                    name: 'survey_type',
                    required: true,
                    label: 'type',
                    align: 'left',
                    field: 'survey_type',
                    sortable: true,
                },
                {
                    name: 'qty_survey_questions',
                    required: true,
                    label: 'questions_quantity',
                    align: 'left',
                    field: 'qty_survey_questions',
                    sortable: true,
                },
                {
                    name: 'start_date',
                    required: true,
                    label: 'start_date',
                    align: 'left',
                    field: 'start_date',
                    sortable: true,
                },
                {
                    name: 'end_date',
                    required: true,
                    label: 'end_date',
                    align: 'left',
                    field: 'end_date',
                    sortable: true,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: true,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'edit',
                    sortable: false,
                },
            ],
            action_id: '',
            confirm_remove: false,
            show_report_survey: false,
            id: '',
            title: ''
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            user_clients: 'common/user_clients',
            areas: 'registration_lists/country_areas',
            user_groups: 'registration_lists/group_users',
            profiles: 'registration_lists/profiles',
            tags: 'registration_lists/tags',
            reports: 'registration_lists/reports',
            survey_types: 'registration_lists/survey_types',
            num_rows: 'datatable/num_rows'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        }
    },

    methods: {
        request ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "ordering": order_by
            }

            this.savePagination()

            this.$axios.get(`/admin_surveys/surveys/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count

                this.surveys = response.data.results.map(r => {
                    return {
                        ...r, actions: r.id,
                        permission: {
                            areas: r.areas,
                            clients: r.clients,
                            user_groups: r.user_groups,
                            profiles: r.profiles
                        }
                    }
                })
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.loading = false
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.get('/accounts/get_registration_lists/', this.getHeader)
            .then(response => {
                this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
                console.log('response pesquisas', response.data)
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.$q.loading.show()

            this.$axios.delete(`/admin_surveys/surveys/${this.action_id}/`, this.getHeader)
            .then(response => {
                this.request({
                    pagination: this.server_pagination
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        },

        showReport(survey) {
            this.id = survey.id
            this.title = survey.title
            this.show_report_survey = true
        },

        hideReport() {
            this.show_report_survey = false
            this.id = ''
            this.title = ''
        },

        getStatus(status) {
            if (status == 1) return 'published_f'
            else if (status == 2) return 'unpublished_f'
            else if (status == 3) return 'deleted_f'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-check'
            else if (status == 2) return 'fas fa-ban'
            else if (status == 3) return 'fas fa-trash-alt'
        },

        getColorStatus(status) {
            if (status == 1) return 'positive'
            else if (status == 2) return 'warning'
            else if (status == 3) return 'negative'
        },

        getQuantity(permission) {
            return permission.areas.length + permission.profiles.length
        },

        getArea(area) {
            let obj = this.areas.find((obj) => { return obj.value === area })
            if (obj) return obj.label
            else return ''
        },

        getClient(client) {
            if (this.user_clients) {
                let obj = this.user_clients.find((obj) => { return obj.id === client })
                if (obj) return obj.name
            }
            return ''
        },

        getUserGroup(user_group) {
            let obj = this.user_groups.find((obj) => { return obj.value === user_group })
            if (obj) return obj.label
            else return ''
        },

        getUserProfile(profile) {
            let obj = this.profiles.find((obj) => { return obj.value === profile })
            if (obj) return obj.label
            else return ''
        },

        getSurveyType(type) {
            let obj = this.survey_types.find((obj) => { return obj.value === type })
            if (obj) return obj.label
            else return ''
        },

        fDate(d) {
            if (d) return moment(d).tz('Etc/GMT').format('DD/MM/YYYY')
        },

        savePagination() {
            this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
        },

        loadPagination() {
            this.server_pagination.rowsPerPage = this.num_rows
        }
    },

    created() {
        this.loadPagination()
        this.fetchLists()
    }
}
</script>

<style lang="stylus" scoped>

</style>
